import type { SafeAny, TrulyString } from '@/common/types';
import type { PreferredView } from '@/modules/user/types';

export enum SignalOverflow {
  All = 'all',
  None = 'none'
}

export enum EpochGraphTypes {
  default = 0,
  oneLine = 1,
  multipleLines = 2,
  bar = 3,
  scatter = 4
}

export type EpochModifierType = 'epoch' | 'epochSize';

export interface SignalConfig {
  defaultOffset: number;
  defaultScale: number;
  max: number;
  min: number;
  unit: string;
  vizmode?: EpochGraphTypes;
}

export interface EpochData {
  mapping: {
    [key: string]: SignalConfig;
  };
  signal: {
    [key: string]: ([number] | [number, number])[];
  };
}

export interface EpochParams {
  id: string;
  epochSize: SafeAny;
  notch?: TrulyString;
  epochSelected: SafeAny;
}

export interface ScoreSetEvent {
  id: string;
  start: string;
  duration: number;
  startEpoch: number;
  endEpoch: number;
}

export interface ScoreSetEventsData {
  [key: string]: ScoreSetEvent[];
}

export interface ShortcutEntry {
  id: string;
  key: string;
}

export interface EventColor {
  eventType: string;
  color: string;
}

export interface ShortcutKey {
  id: string;
  key: string;
}

export interface BandFilter {
  lowCut: number;
  highCut: number;
}

export interface Setting {
  name: string;
  order: number;
  visible: boolean;
  signalColor: string;
  signalLaneHeight?: number;
  lineColor?: string; // defaults to '#000000'
  isOverflowing?: boolean; // defaults to false
  panel?: string; // 'top' | 'bottom'
  eventColors?: EventColor[];
  bandFilter?: BandFilter;
}

export interface DualTimeBaseSettings {
  topWindowSize?: number;
  bottomWindowSize?: number;
  singleHypnogram?: boolean; // defaults to false
}

export interface Preset {
  id: number;
  name: string;
  band: boolean; // defaults to true
  notch: boolean; // defaults to true
  signalOverflow?: string; // defaults to 'none'
  signalsList: Setting[];
  fullNightSignals: Setting[];
  hypnogramFullNightSignals: Setting[];
  viewMode?: string; // 'single' | 'dual'
  dualTimeBase?: DualTimeBaseSettings;
  artifactColor?: string;
  shortcutKeys?: ShortcutKey[];
}

export interface UserResponse {
  id: string;
  presets: Preset[];
  selectedPresetId: number;
  analystName?: string;
  analystCertifications?: string;
  preferredView?: string;
}

export interface UpdateUserPayload {
  selectedPresetId?: number;
  presets?: Preset[];
  preferredView?: PreferredView;
}
